import * as Sentry from '@sentry/nextjs';
import { Dispatch, SetStateAction } from 'react';

import { SelectedItemType } from '../types';

import { type handleAddItemsToCart } from './handleAddItemsToCart';
import { type handleRemoveItemFromCart } from './handleRemoveItemFromCart';
import { type handleUpdateQuantity } from './handleUpdateQuantity';

// When user clicks "Add to cart" we need to figure out which items need to be
// removed from cart, updated in quantity or added to cart. We then call them
// sequentially so we are always operating on the updated cart.
export const handleUpdatingPromoGifts = async (
  handleAddItemsToCartCallback: (
    itemsToAdd: SelectedItemType[],
    showMiniCart?: boolean
  ) => ReturnType<typeof handleAddItemsToCart>,
  handleRemoveItemFromCartCallback: (
    itemsToRemove: SelectedItemType[]
  ) => ReturnType<typeof handleRemoveItemFromCart>,
  handleUpdateQuantityCallback: (
    itemsToUpdate: SelectedItemType[]
  ) => ReturnType<typeof handleUpdateQuantity>,
  closeModal: () => void,
  selectedItems: SelectedItemType[],
  selectedItemsInCartMemo: SelectedItemType[],
  setLoading: Dispatch<SetStateAction<boolean>>,
  showMiniCart?: boolean
) => {
  setLoading(true);
  const itemsToAdd: SelectedItemType[] = [];
  const itemsToUpdate: SelectedItemType[] = [];

  const selectedItemsProductIDs = selectedItems.map(i => i.size.productId);
  const itemsToRemove: SelectedItemType[] = selectedItemsInCartMemo.filter(
    s => !selectedItemsProductIDs.includes(s.size.productId)
  );

  selectedItems.forEach(item => {
    const matchInCart = selectedItemsInCartMemo.find(i => {
      return i.size.productId === item.size.productId;
    });

    // Already in cart, no need to add again.
    if (matchInCart && matchInCart.quantity === item.quantity) {
      return;
    } else if (matchInCart && matchInCart.quantity !== item.quantity) {
      itemsToUpdate.push(item);
    } else {
      itemsToAdd.push(item);
    }
  });

  try {
    setLoading(true);
    // Remove items first so there will not be conflict on max items reached.
    if (itemsToRemove.length) {
      await handleRemoveItemFromCartCallback(itemsToRemove);
    }
    if (itemsToUpdate.length) {
      await handleUpdateQuantityCallback(itemsToUpdate);
    }
    if (itemsToAdd.length) {
      await handleAddItemsToCartCallback(itemsToAdd, showMiniCart);
    }
  } catch (error) {
    Sentry.captureMessage('Error updating promo gifts');
    Sentry.captureException(error);
  } finally {
    closeModal();
    setLoading(false);
  }
};
